import React, { useEffect, useMemo, useRef, useState } from 'react'
import { InputField, Button, Tooltip } from '@jobtarget/ui-library'
import Spinner from './Spinner'
import Alert from './Alert'
import useResponsive from '../hooks/useResponsive'
import { FormBuilderService } from '../api/FormBuilderService'
import '../components/css/SendInviteModal.css'
import { createApplication } from '../api/InviteService'

interface SendInviteModalProps {
  isOpen: boolean
  onClose: () => void
  jobs?: Array<{ id: string; title: string }>
  questionnaires?: Array<{ id: string; name: string; questions?: string[]; contentType?: string }>
  isLoadingJobs?: boolean
  onSuccess?: () => void
}

// Custom Search Dropdown Component
const JobSearchDropdown: React.FC<{
    jobOptions: Array<{ text: string; value: string; id: string }>
    selectedJobId: string
    onJobChange: (value: string) => void
    isLoadingJobs?: boolean
    error?: { hasError: boolean, message: string }
  }> = ({ jobOptions, selectedJobId, onJobChange, isLoadingJobs, error }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
  
    // Filter jobs based on search term
    const filteredJobs = jobOptions
      .filter(job => 
        job.text.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        // Selected job goes to top
        if (a.value === selectedJobId) return -1;
        if (b.value === selectedJobId) return 1;
        return 0; // Keep original order for non-selected items
      });
  
    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false)
          setSearchTerm('') // Reset search when closing
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])
  
    // Focus search input when dropdown opens
    useEffect(() => {
      if (isDropdownOpen && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus()
        }, 100)
      }
    }, [isDropdownOpen])
  
    const handleJobSelect = (jobValue: string) => {
      onJobChange(jobValue)
      setIsDropdownOpen(false)
      setSearchTerm('') // Reset search term after selection
    }
  
    const selectedJobTitle = jobOptions.find(job => job.value === selectedJobId)?.text || ''
  
    return (
      <div className="position-relative" ref={dropdownRef}>
        <label className="form-label mb-2" style={{ fontWeight: 500 }}>Jobs</label>
        
        {/* Main dropdown trigger field */}
        <div 
          className="form-control d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <span className='text-dark'>
            {selectedJobTitle}
          </span>
          <span style={{ fontSize: '12px', color: '#6c757d' }}>
            {isDropdownOpen ? <img src="/svgs/arrow-up.svg" alt="Arrow Up" /> : <img src="/svgs/arrow-down.svg" alt="Arrow Down" />}
            
          </span>
        </div>
  
        {/* Dropdown with search inside */}
        {isDropdownOpen && (
          <div 
            className="position-absolute w-100 bg-white border rounded shadow-lg mt-1"
            style={{ 
              zIndex: 1060,
              maxHeight: '350px',
              overflowY: 'hidden'
            }}
          >
            {/* Search input inside dropdown */}
            <div className="p-3 border-bottom">
              <div className="position-relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="form-control"
                  placeholder="Search" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '2.5rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}
                />
                {/* Search icon inside dropdown search */}
                <img 
                  src="/svgs/search.svg" 
                  alt="Search"
                  className="position-absolute"
                  draggable={false}
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    opacity: 0.6
                  }}
                />
              </div>
            </div>
  
            {/* Scrollable job list */}
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {isLoadingJobs ? (
                <div className="px-3 py-4 text-muted text-center">
                  {/* <div>Loading jobs...</div> */}
                  <Spinner color="primary" />
                </div>
              ) : filteredJobs.length > 0 ? (
                filteredJobs.map((job, index) => (
                  <div
                    key={job.value}
                    className={`px-3 py-3 cursor-pointer position-relative`}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: selectedJobId === job.value ? '#e7f3ff' : 'white',
                      width: '97%',
                      marginLeft: '1.5%',
                      marginTop: '1.5%',
                      borderRadius: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedJobId !== job.value) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa'
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = selectedJobId === job.value ? '#e7f3ff' : 'white'
                    }}
                    onClick={() => handleJobSelect(job.value)}
                  >
                    {selectedJobId === job.value && (
                      <div 
                        className="position-absolute" 
                        style={{ 
                          top: '8px', 
                          right: '12px' 
                        }}
                      >
                        <img src="/svgs/bluecheck.svg" alt="Check" />
                      </div>
                    )}
                    
                    <div>
                      <div className="text-dark mb-1" style={{ fontWeight: 500, color: "#343a40" }}>{job.text}</div>
                      <div className="text-muted">Job ID: {job.id}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-muted text-center">
                  <div>No jobs found</div>
                  <div className="small">Try a different search term</div>
                </div>
              )}
            </div>
          </div>
        )}
        {error && error.hasError && (
          <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
            {error.message}
          </div>
        )}
      </div>
    )
  }

// Custom Questionnaire Search Dropdown Component
const QuestionnaireSearchDropdown: React.FC<{
  questionnaireOptions: Array<{ text: string; value: string; id: string }>
  selectedQuestionnaireId: string
  onQuestionnaireChange: (value: string) => void
  onClose: () => void
}> = ({ questionnaireOptions, selectedQuestionnaireId, onQuestionnaireChange, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(true) // Open by default since it's triggered by Change button
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Filter questionnaires based on search term
  const filteredQuestionnaires = questionnaireOptions
    .filter(questionnaire => 
      questionnaire.text.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Selected questionnaire goes to top
      if (a.value === selectedQuestionnaireId) return -1;
      if (b.value === selectedQuestionnaireId) return 1;
      return 0; // Keep original order for non-selected items
    });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // Focus search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [])

  const handleQuestionnaireSelect = (questionnaireValue: string) => {
    onQuestionnaireChange(questionnaireValue)
    onClose()
  }

  const selectedQuestionnaireTitle = questionnaireOptions.find(q => q.value === selectedQuestionnaireId)?.text || ''

  return (
    <div className="position-relative" ref={dropdownRef}>
      <label className="form-label mb-2" style={{ fontWeight: 500 }}>Select Questionnaire</label>
      
      {/* Dropdown with search */}
      <div 
        className="position-relative w-100 bg-white border rounded shadow-lg"
        style={{ 
          zIndex: 1070, // Higher than job dropdown
          maxHeight: '350px',
          overflowY: 'hidden'
        }}
      >
        {/* Search input inside dropdown */}
        <div className="p-3 border-bottom">
          <div className="position-relative">
            <input
              ref={searchInputRef}
              type="text"
              className="form-control"
              placeholder="Search questionnaires..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: '2.5rem',
                border: '1px solid #dee2e6',
                borderRadius: '4px'
              }}
            />
            {/* Search icon */}
            <img 
              src="/svgs/search.svg" 
              alt="Search"
              className="position-absolute"
              draggable={false}
              style={{
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                opacity: 0.6
              }}
            />
          </div>
        </div>

        {/* Scrollable questionnaire list */}
        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
          {filteredQuestionnaires.length > 0 ? (
            filteredQuestionnaires.map((questionnaire) => (
              <div
                key={questionnaire.value}
                className={`px-3 py-3 cursor-pointer position-relative`}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedQuestionnaireId === questionnaire.value ? '#e7f3ff' : 'white',
                  width: '97%',
                  marginLeft: '1.5%',
                  marginTop: '1.5%',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  if (selectedQuestionnaireId !== questionnaire.value) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa'
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selectedQuestionnaireId === questionnaire.value ? '#e7f3ff' : 'white'
                }}
                onClick={() => handleQuestionnaireSelect(questionnaire.value)}
              >
                {selectedQuestionnaireId === questionnaire.value && (
                  <div 
                    className="position-absolute" 
                    style={{ 
                      top: '8px', 
                      right: '12px' 
                    }}
                  >
                    <img src="/svgs/bluecheck.svg" alt="Check" />
                  </div>
                )}
                
    <div>
                  <div className="text-dark mb-0" style={{ fontWeight: 500, color: "#343a40" }}>{questionnaire.text}</div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-muted text-center">
              <div>No questionnaires found</div>
              <div className="small">Try a different search term</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Questionnaire Preview Container Component
const QuestionnairePreviewContainer: React.FC<{
  selectedJobId: string
  isMobile: boolean
  selectedQuestionnaireId: string
  showQuestionnaireSelector: boolean
  setShowQuestionnaireSelector: (show: boolean) => void
  setSelectedQuestionnaireId: (id: string) => void
  availableQuestionnaires: Array<{ id: string; name: string; questions: string[]; contentType?: string }>
  jobQuestionnaire: any
  isLoadingQuestionnaire: boolean
  questionnaireError: string
}> = ({ 
  selectedJobId, 
  isMobile, 
  selectedQuestionnaireId, 
  showQuestionnaireSelector, 
  setShowQuestionnaireSelector,
  setSelectedQuestionnaireId,
  availableQuestionnaires,
  jobQuestionnaire,
  isLoadingQuestionnaire,
  questionnaireError
}) => {
  const selectedQuestionnaire = availableQuestionnaires.find(q => q.id === selectedQuestionnaireId) || availableQuestionnaires[0]

  return (
    <div className="bg-white rounded shadow-sm p-4 h-100">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Questionnaire Preview</h5>
      </div>
      
      {/* Gray preview box - single layout */}
      <div 
        className="rounded p-3 mb-3 border"
        style={{ backgroundColor: '#f8f9fa', color: '#343a40' }}
      >
        <p className="mb-1 small">
          {selectedJobId ? (
            selectedQuestionnaireId !== 'default' && selectedQuestionnaire 
              ? `${selectedQuestionnaire.contentType || 'Video'} Screener`
              : (jobQuestionnaire ? `${jobQuestionnaire.contentType || 'Video'} Screener` : 'Video Screener')
          ) : 'Video Screener'}
        </p>
        <div className="d-flex justify-content-between align-items-center">
        {isMobile ? (
          <h6 className="mb-0">
            {selectedJobId ? (
              selectedQuestionnaireId !== 'default' && selectedQuestionnaire 
                ? selectedQuestionnaire.name 
                : (jobQuestionnaire ? jobQuestionnaire?.questionnaireName : 'No questionnaire attached')
            ) : 'Select a Job to Start Preview'}
          </h6>
        ) : (
          <h5 className="mb-0">
            {selectedJobId ? (
              selectedQuestionnaireId !== 'default' && selectedQuestionnaire 
                ? selectedQuestionnaire.name 
                : (jobQuestionnaire ? jobQuestionnaire?.questionnaireName : 'No questionnaire attached')
            ) : 'Select a Job to Start Preview'}
          </h5>
        )}

          {selectedJobId && (
          <button 
            className="btn btn-link p-0 text-primary" 
            style={{ textDecoration: 'none', fontSize: isMobile ? '0.8rem' : '14px' }}
            onClick={() => setShowQuestionnaireSelector(!showQuestionnaireSelector)}
          >
            Change
          </button>
          )}
        </div>
      </div>

      {selectedJobId && <hr className="my-4"></hr>}

      {/* Questionnaire Selector - shows when Change button is clicked */}
      {showQuestionnaireSelector && (
        <div className="mb-3">
          <QuestionnaireSearchDropdown
            questionnaireOptions={availableQuestionnaires.map(q => ({ 
              text: q.name, 
              value: q.id,
              id: q.id 
            }))}
            selectedQuestionnaireId={selectedQuestionnaireId}
            onQuestionnaireChange={(value) => {
              setSelectedQuestionnaireId(value)
              setShowQuestionnaireSelector(false)
            }}
            onClose={() => setShowQuestionnaireSelector(false)}
          />
        </div>
      )}

      {/* Questions preview - only show if job is selected and selector is not open */}
      {selectedJobId && !showQuestionnaireSelector && (
        <div style={{ fontSize: '1rem' }}>
          {isLoadingQuestionnaire ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <div className="text-muted mt-2">Loading questionnaire...</div>
            </div>
          ) : questionnaireError && selectedQuestionnaireId === 'default' ? (
            <div className="text-muted text-center py-4">
              <div>{questionnaireError}</div>
            </div>
          ) : jobQuestionnaire || (selectedQuestionnaireId !== 'default' && selectedQuestionnaire) ? (
            // Show questions from selected questionnaire, not always jobQuestionnaire
            (() => {
              // If a custom questionnaire is selected, use that; otherwise use the job's questionnaire
              const questionsToShow = selectedQuestionnaireId !== 'default' && selectedQuestionnaire 
                ? selectedQuestionnaire.questions 
                : jobQuestionnaire.questions;
              
              return questionsToShow.map((question: any, index: number) => (
                <div key={index} className={index === questionsToShow.length - 1 ? "mb-0" : "mb-4"}>
                  {index + 1}. {typeof question === 'string' ? question : question.questionText}
                </div>
              ))
            })()
          ) : null}
        </div>
      )}
      
    </div>
  )
}

const getTextWidth = (text: string, inputElement?: HTMLInputElement) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (context) {
    let fontSize = '0.875rem'
    let fontFamily = 'inherit'
    
    // Get actual font properties if input element is provided
    if (inputElement) {
      const styles = window.getComputedStyle(inputElement)
      fontSize = styles.fontSize
      fontFamily = styles.fontFamily
    }
    
    context.font = `${fontSize} ${fontFamily}`
    return context.measureText(text).width
  }
  return 0
}

const getInputWidth = (inputElement: HTMLInputElement | null) => {
  if (inputElement) {
    const rect = inputElement.getBoundingClientRect()
    return rect.width
  }
  return 0
}

// Send To Container Component
const SendToContainer: React.FC<{
    formData: any
    onInputChange: (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => void
    onKeyDown: (field: string) => (e: React.KeyboardEvent<HTMLInputElement>) => void
    onPaste: (field: string) => (e: React.ClipboardEvent<HTMLInputElement>) => void
    onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    onSubmit: (e: React.FormEvent) => void
    isMobile: boolean
    emailError: { hasError: boolean, message: string }
    fileError: { hasError: boolean, message: string }
    phoneError: { hasError: boolean, message: string }
    isSubmitting?: boolean
  }> = ({ formData, onInputChange, onKeyDown, onPaste, onFileChange, onSubmit, isMobile, emailError, fileError, phoneError, isSubmitting = false }) => {
    const [inputWidths, setInputWidths] = useState({
      firstName: 0,
      lastName: 0,
      email: 0,
      firstNameInput: null as HTMLInputElement | null,
      lastNameInput: null as HTMLInputElement | null,
      emailInput: null as HTMLInputElement | null
    })

    // Use refs instead of callback refs
    const firstNameRef = useRef<any>(null)
    const lastNameRef = useRef<any>(null)
    const emailRef = useRef<any>(null)

    // Measure widths once after component mounts
    useEffect(() => {
      const measureWidths = () => {
        const measurements = { 
          firstName: 0, 
          lastName: 0, 
          email: 0, 
          firstNameInput: null,
          lastNameInput: null,
          emailInput: null
        }
        
        if (firstNameRef.current) {
          const inputElement = firstNameRef.current.querySelector('input') || firstNameRef.current
          measurements.firstName = getInputWidth(inputElement)
          measurements.firstNameInput = inputElement
        }
        
        if (lastNameRef.current) {
          const inputElement = lastNameRef.current.querySelector('input') || lastNameRef.current
          measurements.lastName = getInputWidth(inputElement)
          measurements.lastNameInput = inputElement
        }
        
        if (emailRef.current) {
          const inputElement = emailRef.current.querySelector('input') || emailRef.current
          measurements.email = getInputWidth(inputElement)
          measurements.emailInput = inputElement
        }
        
        setInputWidths(measurements)
      }
    
      const timer = setTimeout(measureWidths, 200)
      return () => clearTimeout(timer)
    }, [])

    return (
      <>
        <h5 className="mb-4">Send To</h5>
        <form onSubmit={onSubmit}>
          <div className={`row ${isMobile ? 'g-3' : 'g-4'}`}>
            {/* First Name & Last Name */}
            <div className={`${isMobile ? 'col-12 mb-3' : 'col-md-6 mb-3'}`}>
              <div className="position-relative">
                <InputField
                  ref={firstNameRef}
                  id="first-name"
                  name="firstName"
                  placeholder="First Name"
                  required
                  value={formData.firstName}
                  onChange={onInputChange('firstName')}
                  onPaste={onPaste('firstName')}
                  error={{
                    hasError: false,
                    message: ''
                  }}
                />
                {!formData.firstName && ( // Only show if field is empty
                  <Tooltip message="This field is required" position="right">
                    <span className="position-absolute" style={{ 
                      left: `${12 + getTextWidth('First Name', inputWidths.firstNameInput as HTMLInputElement)}px`,
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#dc3545',
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      pointerEvents: 'auto',
                      cursor: 'help',
                      // backgroundColor: 'green'
                    }}>*</span>
                  </Tooltip>
                )}
              </div>
            </div>
            <div className={`${isMobile ? 'col-12 mb-3' : 'col-md-6 mb-3'}`}>
              <div className="position-relative">
                <InputField
                  ref={lastNameRef}
                  id="last-name"
                  name="lastName"
                  placeholder="Last Name"
                  required
                  value={formData.lastName}
                  onChange={onInputChange('lastName')}
                  onPaste={onPaste('lastName')}
                  error={{
                    hasError: false,
                    message: ''
                  }}
                />
                {!formData.lastName && (
                  <Tooltip message="This field is required" position="right">
                    <span className="position-absolute" style={{ 
                      left: `${12 + getTextWidth('Last Name', inputWidths.lastNameInput as HTMLInputElement)}px`,
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#dc3545',
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      pointerEvents: 'auto',
                      cursor: 'help',
                    }}>*</span>
                  </Tooltip>
                )}
              </div>
            </div>
  
            {/* Email */}
            <div className="col-12 mb-3">
              <div className="position-relative">
                <InputField
                  ref={emailRef}
                  id="email"
                  name="email"
                  type="text"
                  placeholder="Email"
                  required
                  value={formData.email}
                  onChange={onInputChange('email')}
                  onPaste={onPaste('email')}
                  error={{
                    hasError: emailError.hasError,
                    message: emailError.message
                  }}
                />
                {!formData.email && (
                  <Tooltip message="This field is required" position="right">
                    <span className="position-absolute" style={{ 
                      left: `${12 + getTextWidth('Email', inputWidths.emailInput as HTMLInputElement)}px`,
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      color: '#dc3545',
                      fontSize: isMobile ? '1rem' : '1.2rem',
                      pointerEvents: 'auto',
                      cursor: 'help',
                    }}>*</span>
                  </Tooltip>
                )}
              </div>
            </div>
  
            {/* Phone Number */}
            <div className="col-12 mb-3">
              <InputField
                id="phone"
                name="phone"
                type="text"
                placeholder="Phone Number (Optional)"
                value={formData.phone}
                onChange={onInputChange('phone')}
                onKeyDown={onKeyDown('phone')}
                onPaste={onPaste('phone')}
                error={{
                  hasError: phoneError.hasError,
                  message: phoneError.message
                }}
              />
            </div>
  
            {/* Resume Upload */}
            <div className="col-12 mb-4">
                <div className="position-relative">
                    <input 
                        type="file" 
                        className="d-none" // Hide the actual file input
                        id="resume-upload"
                        accept=".pdf,.doc,.docx,.txt,.rtf"
                        onChange={onFileChange}
                    />
                    <div className="input-group">
                        <input 
                            type="text"
                            className={`form-control ${fileError.hasError ? 'is-invalid' : ''}`}
                            placeholder="Upload Resume"
                            value={formData.resume ? formData.resume.name : ''}
                            readOnly
                            style={{ 
                                cursor: 'pointer', 
                                backgroundColor: 'white' 
                            }}
                            onClick={() => document.getElementById('resume-upload')?.click()}
                        />
                        <Button 
                            variant="transparent" 
                            type="button"
                            onClick={() => document.getElementById('resume-upload')?.click()}
                            style={{
                                backgroundColor: '#e4e6ef',
                                borderRadius: '0'
                            }}
                        >
                            <img 
                                src="/svgs/file.svg" 
                                alt="File"
                                style={{ 
                                    width: '1rem',
                                    height: '1rem',
                                    marginRight: '0.5rem'
                                }}
                            />
                            Browse...
                        </Button>
                    </div>
                </div>
                {fileError.hasError && (
                    <div className="text-danger small mt-1">
                        {fileError.message}
                    </div>
                )}
                {/* {formData.resume && ( // nice to have but ignore for now
                    <div className="form-text text-success mt-2">
                    Selected: {formData.resume.name}
                    </div>
                )} */}
            </div>
  
            {/* Submit Button */}
            <div className="col-12">
              <Button 
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                style={{ 
                  width: '100%',
                  padding: '0.875rem',
                  fontSize: '1rem',
                  fontWeight: '500'
                }}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" color="light" />
                    <span style={{ marginLeft: '0.5rem' }}>Sending...</span>
                  </>
                ) : (
                  <>
                    Send Invite
                    <img 
                      src="/svgs/paperplane.svg" 
                      alt="Send"
                      style={{ 
                        width: '1.3rem',
                        height: '1.3rem',
                        filter: 'brightness(0) invert(1)',
                        marginLeft: '0.5rem'
                      }}
                    />
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
    </>
    )
  }

// Main Modal Component (Clean & Organized)
const SendInviteModal: React.FC<SendInviteModalProps> = ({ 
  isOpen, 
  onClose,
  onSuccess,
  jobs = [],
  questionnaires = [],
  isLoadingJobs = false
}) => {
  const isMobileOrTablet = useResponsive('lg');
  
  const initialFormData = {
    jobId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    resume: null as File | null,
  };

  const [formData, setFormData] = useState(initialFormData);

  const [emailError, setEmailError] = useState({ hasError: false, message: '' })
  const [fileError, setFileError] = useState({ hasError: false, message: '' })
  const [phoneError, setPhoneError] = useState({ hasError: false, message: '' })
  const [jobError, setJobError] = useState({ hasError: false, message: '' })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState('default')
  const [showQuestionnaireSelector, setShowQuestionnaireSelector] = useState(false)

  const [jobQuestionnaire, setJobQuestionnaire] = useState(null)
  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState(false)
  const [questionnaireError, setQuestionnaireError] = useState('')

  // Questionnaires
  const availableQuestionnaires = useMemo(() => {
    if (questionnaires && questionnaires.length > 0) {
      return questionnaires.map(q => ({
        id: q.id,
        name: q.name,
        contentType: q.contentType || 'Video',
        questions: q.questions || [] // empty fallback
      }))
    }
  }, [questionnaires])

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState<boolean>(localStorage.getItem('showAlert') === 'true'); // testing purpose

  // use for fetching questionnaires for that job
  useEffect(() => {
    const checkJobQuestionnaire = async () => {
      if (!formData.jobId) {
        setJobQuestionnaire(null)
        setQuestionnaireError('')
        return
      }
  
      setIsLoadingQuestionnaire(true)
      setQuestionnaireError('')
      
      try {
        const result = await FormBuilderService.checkJobQuestionnaire(parseInt(formData.jobId))
        
        if (result.hasQuestionnaire) {
          setJobQuestionnaire(result.data)
          setQuestionnaireError('')
        } else {
          setJobQuestionnaire(null)
          setQuestionnaireError('No questionnaire attached to this job yet')
        }
      } catch (error) {
        console.error('Error checking job questionnaire:', error)
        setJobQuestionnaire(null)
        setQuestionnaireError('Error loading questionnaire')
      } finally {
        setIsLoadingQuestionnaire(false)
      }
    }
  
    checkJobQuestionnaire()
  }, [formData.jobId])

  useEffect(() => {
    if (formData.jobId) {
      setSelectedQuestionnaireId('default') // Reset to show job's questionnaire
      setShowQuestionnaireSelector(false) // Close selector if open
    }
  }, [formData.jobId])

  // Email validation function
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      return { hasError: false, message: '' } // reset
    }
    if (!emailRegex.test(email)) {
      return { hasError: true, message: 'Please enter a valid email address' }
    }
    return { hasError: false, message: '' }
  }

  const validateFile = (file: File | null) => {
    if (!file) {
      return { hasError: false, message: '' }
    }
    
    const allowedTypes = [
      'application/pdf', // PDF
      'application/msword', // DOC
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
      'application/rtf', // RTF
      'text/plain' // TXT
    ]
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.rtf', '.txt']
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return { 
        hasError: true, 
        message: 'Please select a PDF, DOC, DOCX, RTF, or TXT file only' 
      }
    }
    
    // Optional: Check file size (e.g., max 10MB)
    const maxSize = 10 * 1024 * 1024 // 10MB in bytes
    if (file.size > maxSize) {
      return { 
        hasError: true, 
        message: 'File size must be less than 10MB' 
      }
    }
    
    return { hasError: false, message: '' }
  }

  const validatePhone = (phone: string) => {
    if (!phone) {
      return { hasError: false, message: '' } // Phone is not required
    }
    
    const phoneRegex = /^[0-9-]+$/
    if (!phoneRegex.test(phone)) {
      return { hasError: true, message: 'Phone number can only contain numbers and dashes' }
    }
    
    return { hasError: false, message: '' }
  }

  const validateJobQuestionnaire = (jobId: string, jobQuestionnaire: any) => {
    if (!jobId) {
      return { hasError: true, message: 'Please select a job' }
    }
    
    if (!jobQuestionnaire) {
      return { hasError: true, message: 'Selected job must have a questionnaire attached' }
    }
    
    return { hasError: false, message: '' }
  }

  // Resets form
  const resetForm = () => {
    setFormData(initialFormData);
    setEmailError({ hasError: false, message: '' });
    setFileError({ hasError: false, message: '' });
    setPhoneError({ hasError: false, message: '' });
    setJobError({ hasError: false, message: '' });
    setSelectedQuestionnaireId('default');
    setShowQuestionnaireSelector(false);
    setJobQuestionnaire(null);
    setQuestionnaireError('');
  };

  // Jobs
  const jobOptions = useMemo(() => {
    if (jobs && jobs.length > 0) {
      return jobs.map(job => ({
        text: job.title,
        value: job.id.toString(),
        id: job.id.toString()
      }))
    }
  }, [jobs])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate email before submit
    const emailValidation = validateEmail(formData.email)
    setEmailError(emailValidation)
    
    // Validate file before submit
    const fileValidation = validateFile(formData.resume)
    setFileError(fileValidation)

    // Validate phone before submit
    const phoneValidation = validatePhone(formData.phone)
    setPhoneError(phoneValidation)
    
    // Validate job questionnaire before submit
    const jobValidation = validateJobQuestionnaire(formData.jobId, jobQuestionnaire)
    setJobError(jobValidation)
    
    if (emailValidation.hasError || fileValidation.hasError || phoneValidation.hasError || jobValidation.hasError) {
      console.warn('Form has validation errors')
      return
    }

    setIsSubmitting(true)

    try {
      console.log('Creating application with data:', formData)
      
      const result = await createApplication({
        jobId: formData.jobId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || undefined,
        resume: formData.resume
      });
  
      if (result.success) {
        console.log('Application created successfully:', result);
        onSuccess?.();
        handleClose();
      } else {
        console.error('Failed to create application:', result.error);
        
        let errorMessage = result.error || 'Failed to create application. Please try again.';

        if (result.statusCode === 409) {
          if (result.error?.includes('already been invited')) {
            errorMessage = 'This applicant has already been invited to take the screener for this job.';
          } else if (result.error?.includes('already in our system')) {
            errorMessage = 'This applicant already has an application in the system.';
          } else {
            errorMessage = 'This applicant has already been processed for this position.';
          }
        }

        alert(errorMessage);
      }

      resetForm()

    } catch (error) {
      console.error('Error submitting application:', error);
      alert('An error occurred while creating the application. Please try again.');
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value

    if (field === 'phone') {
      value = value.replace(/[^0-9-]/g, '') // Remove any character that's not a number or dash
    }

    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Validate email on change
    if (field === 'email') {
      const validation = validateEmail(value)
      setEmailError(validation)
    }

    // Validate phone on change
    if (field === 'phone') {
      const validation = validatePhone(value)
      setPhoneError(validation)
    }
  }

  const handleKeyDown = (field: string) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (field === 'phone') {
      // Allow numbers (0-9), dash (-), backspace, delete, tab, escape, enter, and arrow keys
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']

      // Allow Ctrl+A (select all) and Ctrl+V (paste)
      if (e.ctrlKey && (e.key === 'a' || e.key === 'v')) {
        return
      }
      
      if (allowedKeys.includes(e.key)) {
        return // Allow these keys
      }
      
      // Only allow numbers and dash
      if (!/[0-9-]/.test(e.key)) {
        e.preventDefault() // Block the keypress
      }
    }
  }

  const handlePaste = (field: string) => (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    let filteredText = pastedText
    
    switch (field) {
      case 'phone':
        // Filter to only allow numbers and dashes
        filteredText = pastedText.replace(/[^0-9-]/g, '')
        console.log('Filtered phone text:', filteredText)
        // Update form data
        setFormData(prev => ({ ...prev, [field]: filteredText }))
        // Validate
        const phoneValidation = validatePhone(filteredText)
        setPhoneError(phoneValidation)
        break
        
      case 'firstName':
      case 'lastName':
        // Filter to only allow letters, spaces, apostrophes, and hyphens
        filteredText = pastedText.replace(/[^a-zA-Z\s'-]/g, '').trim()
        setFormData(prev => ({ ...prev, [field]: filteredText }))
        break
        
      case 'email':
        // Remove spaces and convert to lowercase
        filteredText = pastedText.trim().toLowerCase().replace(/\s/g, '')
        setFormData(prev => ({ ...prev, [field]: filteredText }))
        // Validate email
        const emailValidation = validateEmail(filteredText)
        setEmailError(emailValidation)
        break
        
      default:
        setFormData(prev => ({ ...prev, [field]: pastedText }))
        break
    }
  }

  const handleSelectChange = (value: string) => {
    setFormData(prev => ({ ...prev, jobId: value }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    // Validate file
    const validation = validateFile(file)
    setFileError(validation)
    
    if (validation.hasError) {
      // Clear the file input if invalid
      e.target.value = ''
      setFormData(prev => ({ ...prev, resume: null }))
      return
    }
    
    setFormData(prev => ({ ...prev, resume: file }))
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  useEffect(() => {
    if (formData.jobId && jobQuestionnaire) {
      setJobError({ hasError: false, message: '' })
    } else if (formData.jobId && !isLoadingQuestionnaire && !jobQuestionnaire) {
      setJobError({ hasError: true, message: 'Selected job must have a questionnaire attached' })
    }
  }, [formData.jobId, jobQuestionnaire, isLoadingQuestionnaire])

  if (!isOpen) return null

  return (
    <>
      {/* Dark backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1040 
        }}
        onClick={handleClose}
      />
      
      {isMobileOrTablet ? (
        // NUCLEAR OPTION: Custom full-screen div for mobile/tablet
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1050,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          borderRadius: '12px'
        }}>
          {/* Header */}
          <div className="modal-header border-bottom bg-white d-flex justify-content-between align-items-center">
            <h4 className="modal-title mb-0">Invite Applicant</h4>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              aria-label="Close"
              style={{ 
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img src="/svgs/times.svg" alt="Close" />
            </button>
          </div>

          {/* Alert */}
          {showAlert && (
            <div>
              <Alert
                  variant="dark-warning"
                  message="This candidate is in your Candidate Manager pool."
                  showIcon={true}
                  buttons={[{
                    text: "Go to Candidate Manager",
                    variant: "dark-warning",
                    onClick: () => {console.log("Go to Candidate Manager")},
                    icon: "/svgs/can_man-logo.svg",
                    iconType: "svg"
                  }, {
                    text: "Clear Form",
                    variant: "dark-warning-outline", 
                    onClick: () => {console.log("Clear Form")},
                    icon: "fal fa-trash-can",
                    iconType: "fontawesome"
                  }]}
                  onClose={() => setShowAlert(false)}
                />
            </div>
          )}
  
          {/* Body */}
          <div style={{ 
            padding: '0.4rem',
            flex: 1,
            overflowY: 'auto'
          }}>
            <div className="row g-3 m-0 h-100">
              {/* Left Container */}
              <div className="col-12" style={{ paddingBottom: '1.5rem' }}>
                <div className="bg-white p-3 mb-3 h-100 d-flex flex-column">
                  {/* Job Selection Section */}
                  <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #dee2e6' }}>
                    <h5 className="mb-4">Select Job Post</h5>
                    <JobSearchDropdown
                      jobOptions={jobOptions || []}
                      selectedJobId={formData.jobId}
                      onJobChange={handleSelectChange}
                      isLoadingJobs={isLoadingJobs}
                      error={jobError}
                    />
                  </div>
  
                  {/* Send To Section */}
                  <div>
                    <SendToContainer
                      formData={formData}
                      onInputChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      onFileChange={handleFileChange}
                      onSubmit={handleSubmit}
                      isMobile={isMobileOrTablet}
                      emailError={emailError}
                      fileError={fileError}
                      phoneError={phoneError}
                      isSubmitting={isSubmitting}
                    />
                  </div>
                </div>
              </div>
  
              {/* Right Container */}
              <div className="col-12">
                <QuestionnairePreviewContainer
                  selectedJobId={formData.jobId}
                  isMobile={isMobileOrTablet}
                  selectedQuestionnaireId={selectedQuestionnaireId}
                  showQuestionnaireSelector={showQuestionnaireSelector}
                  setShowQuestionnaireSelector={setShowQuestionnaireSelector}
                  setSelectedQuestionnaireId={setSelectedQuestionnaireId}
                  availableQuestionnaires={availableQuestionnaires || []}
                  jobQuestionnaire={jobQuestionnaire}
                  isLoadingQuestionnaire={isLoadingQuestionnaire}
                  questionnaireError={questionnaireError}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // DESKTOP: Keep original Bootstrap modal structure
        <div 
          className="modal fade show d-block modal-centered" 
          style={{ zIndex: 1050 }}
          tabIndex={-1}
          role="dialog"
          aria-hidden="false"
          onClick={handleClose}
        >
          <div className="modal-dialog modal-xl"
            style={{ 
              maxWidth: '70%',
            }}
            onClick={handleClose}
          >
            <div className="modal-content" style={{
              maxHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
            }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header border-bottom bg-white d-flex justify-content-between align-items-center">
                <h4 className="modal-title mb-0">Invite Applicant</h4>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleClose}
                  aria-label="Close"
                  style={{ 
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img src="/svgs/times.svg" alt="Close" />
                </button>
              </div>

              {/* Alert */}
              {showAlert && (
                <div>
                <Alert
                  variant="dark-warning"
                  message="This candidate is in your Candidate Manager pool."
                  showIcon={true}
                  buttons={[{
                    text: "Go to Candidate Manager",
                    variant: "dark-warning",
                    onClick: () => {console.log("Go to Candidate Manager")},
                    icon: "/svgs/can_man-logo.svg",
                    iconType: "svg"
                  }, {
                    text: "Clear Form",
                    variant: "dark-warning-outline", 
                    onClick: () => {console.log("Clear Form")},
                    icon: "fal fa-trash-can",
                    iconType: "fontawesome"
                  }]}
                  onClose={() => setShowAlert(false)}
                />
              </div>
              )}
  
              <div className="modal-body" style={{ 
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                flex: '1',
                overflowY: 'auto',
                margin: '0',
                minHeight: 0
              }}>
                <div className="row g-4 h-100">
                  {/* Left Container */}
                  <div className="col-lg-6 h-100" style={{ paddingRight: '0' }}>
                    <div className="bg-white rounded shadow-sm p-4">
                      {/* Job Selection Section */}
                      <div className="mb-4 pb-4" style={{ borderBottom: '1px solid #dee2e6' }}>
                        <h5 className="mb-4">Select Job Post</h5>
                        <JobSearchDropdown
                          jobOptions={jobOptions || []}
                          selectedJobId={formData.jobId}
                          onJobChange={handleSelectChange}
                          isLoadingJobs={isLoadingJobs}
                          error={jobError}
                        />
                      </div>
  
                      {/* Send To Section */}
                      <div>
                        <SendToContainer
                          formData={formData}
                          onInputChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          onPaste={handlePaste}
                          onFileChange={handleFileChange}
                          onSubmit={handleSubmit}
                          isMobile={false}
                          emailError={emailError}
                          fileError={fileError}
                          phoneError={phoneError}
                          isSubmitting={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
  
                  {/* Right Container */}
                  <div className="col-lg-6">
                    <QuestionnairePreviewContainer
                      selectedJobId={formData.jobId}
                      isMobile={isMobileOrTablet}
                      selectedQuestionnaireId={selectedQuestionnaireId}
                      showQuestionnaireSelector={showQuestionnaireSelector}
                      setShowQuestionnaireSelector={setShowQuestionnaireSelector}
                      setSelectedQuestionnaireId={setSelectedQuestionnaireId}
                      availableQuestionnaires={availableQuestionnaires || []}
                      jobQuestionnaire={jobQuestionnaire}
                      isLoadingQuestionnaire={isLoadingQuestionnaire}
                      questionnaireError={questionnaireError}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SendInviteModal
